import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core'
import {
  DataContextBuilder,
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderSelectComponent,
  ElderToastService,
  IDataContext,
  Interval,
} from '@elderbyte/ngx-starter'
import { ZoneId } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FacilityContextService } from '../../../context/facility-context/facility-context.service'
import { WorkSchedule } from '../../../schedules/work-schedule'
import { WorkScheduleManagerComponent } from '../../../schedules/work-schedule-manager/work-schedule-manager.component'
import { FacilitySchedule, FacilityScheduleType } from '../facility-schedule'
import { FacilityScheduleFilter, FacilityScheduleService } from '../facility-schedule.service'
import { AsyncPipe } from '@angular/common'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { FacilitySelectDirective } from '../../facility-select.directive'

@Component({
  selector: 'app-facility-work-schedule-detail',
  templateUrl: './facility-schedule-detail.component.html',
  styleUrls: ['./facility-schedule-detail.component.scss'],
  imports: [
    AsyncPipe,
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    FacilitySelectDirective,
    WorkScheduleManagerComponent,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
  ],
})
export class FacilityScheduleDetailComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly facilityId$ = new BehaviorSubject<string>(null)
  public readonly data: IDataContext<FacilitySchedule>
  private facilityWorkScheduleService = inject(FacilityScheduleService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const facilityContextService = inject(FacilityContextService)

    this.data = DataContextBuilder.start<FacilitySchedule>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([FacilityScheduleFilter.FACILITY_IDS_KEY])
      .build(this.facilityWorkScheduleService)

    facilityContextService.facility$.subscribe({
      next: (facility) => {
        if (facility) {
          this.facilityId = facility.id
        }
      },
      error: (err) => this.toastService.error('messages.loading_failed', err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facilityId(facilityId: string) {
    this.facilityId$.next(facilityId)

    if (facilityId) {
      this.data.filter.updateFilter(FacilityScheduleFilter.facilityId(facilityId))
    }
  }

  public get facilityId(): string {
    return this.facilityId$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onCreate(_: Event): void {
    const facilityId = this.facilityId
    if (facilityId) {
      const interval = Interval.tomorrow(ZoneId.systemDefault()).toInstantString()
      const created = new FacilitySchedule(facilityId, 'Neu', FacilityScheduleType.WORK, interval)

      this.create(created)
    } else {
      this.toastService.error('Cannot create new plan', 'The facility or the type is missing!')
    }
  }

  public onDeleteAll(schedules: WorkSchedule[]): void {
    this.facilityWorkScheduleService.deleteAll(schedules as FacilitySchedule[]).subscribe({
      next: (_) => this.toastService.info('messages.deleting_successful'),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  public onDuplicate(schedule: WorkSchedule): void {
    const facilitySchedule = schedule as FacilitySchedule

    const newSchedule = new FacilitySchedule(
      facilitySchedule.facilityId,
      'Kopie',
      facilitySchedule.type,
      facilitySchedule.validityInterval,
      facilitySchedule.weekSchedule
    )

    this.create(newSchedule)
  }

  public onSave(schedule: WorkSchedule): void {
    this.save(schedule as FacilitySchedule)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private create(schedule: FacilitySchedule): void {
    this.facilityWorkScheduleService.create(schedule).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private save(schedule: FacilitySchedule): void {
    this.facilityWorkScheduleService.update(schedule).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }
}
